import React from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import TextField from '@mui/material/TextField';
import Checkbox from 'components/form/Checkbox';
import TextInput from 'components/form/TextInput';
import weekdays from 'constants/weekdays';
import Select from 'components/form/Select';
import Timepicker from 'components/form/TimePicker';

const WEEKDAYS = weekdays.map((day) => ({ label: day, value: day }));

const PopUp = ({ id, popup, onRadioChange, onInputChange }) => {
  const [t] = useTranslation('groups');
  const {
    enabled,
    title,
    buttonText,
    videoUrl,
    waitDays,
    waitUntil,
    countdownType,
    countdownDate,
    countdownDay,
    countdownTime,
  } = popup;

  const isEnabled = Boolean(enabled);
  return (
    <Box>
      <Typography gutterBottom variant="caption" color="textSecondary">
        {t(
          'cc-form-popup-desc',
          'This will display a popup after a certain amount of time.',
        )}
      </Typography>
      {isEnabled && (
        <React.Fragment>
          <TextInput
            fullWidth
            name="collection_settings.popup.title"
            value={title}
            onChange={onInputChange}
            label={t('cc-form-popup-form-title', 'Headline')}
            placeholder={t(
              'cc-form-popup-form-title-placeholder',
              'Ex. Preregister your kids',
            )}
          />
          <TextInput
            fullWidth
            name="collection_settings.popup.buttonText"
            value={buttonText}
            onChange={onInputChange}
            label={t('cc-form-popup-custom-btn', 'Call to Action Button')}
            placeholder={t('cc-form-popup-custom-btn-placeholder', 'Ok!')}
          />
          <TextInput
            fullWidth
            sx={{ mb: 4 }}
            name="collection_settings.popup.videoUrl"
            value={videoUrl}
            onChange={onInputChange}
            label={t(
              'cc-form-popup-form-video',
              'Insert Video URL (Youtube/Vimeo)',
            )}
            placeholder={t(
              'cc-form-popup-form-video-placeholder',
              'Include a video in your popup',
            )}
          />
          <Typography paragraph fontWeight={500}>
            Delay Trigger
          </Typography>
          <Stack direction="row" alignItems="center" spacing={0.5} mb={1}>
            <Typography>Trigger after</Typography>
            <TextInput
              sx={{ width: 70 }}
              margin="none"
              size="compact"
              type="number"
              name="collection_settings.popup.waitUntil"
              value={waitUntil}
              onChange={onInputChange}
              placeholder="eg. 10"
            />
            <Typography>seconds</Typography>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={0.5} mb={3}>
            <Typography>Wait</Typography>
            <TextInput
              sx={{ width: 70 }}
              size="compact"
              margin="none"
              type="number"
              name="collection_settings.popup.waitDays"
              value={waitDays}
              onChange={onInputChange}
              placeholder="eg. 10"
            />
            <Typography whiteSpace="nowrap">days before reshowing</Typography>
          </Stack>
          <Box>
            <Typography fontWeight={500}>Enable Countdown Timer</Typography>
            <Box>
              <Checkbox
                label="Enable Date Timer"
                hint="Set a timer until your next event."
                onChange={(e) =>
                  onInputChange({
                    target: {
                      name: 'collection_settings.popup.countdownType',
                      value: e.target.checked ? 'fixed' : '',
                    },
                  })
                }
                checked={countdownType === 'fixed'}
              />
              {countdownType === 'fixed' && (
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  mt={1}
                  mb={1}
                >
                  <Typography whiteSpace="nowrap">Start date</Typography>
                  <DateTimePicker
                    disablePast
                    value={countdownDate || null}
                    onChange={(v) =>
                      onInputChange({
                        target: {
                          value: v,
                          name: 'collection_settings.popup.countdownDate',
                        },
                      })
                    }
                    renderInput={(props) => (
                      <TextField {...props} size="small" />
                    )}
                  />
                </Stack>
              )}
              <Box>
                <Checkbox
                  label="Enable Weekly Timer"
                  hint="Set a weekly countdown timer."
                  onChange={(e) =>
                    onInputChange({
                      target: {
                        name: 'collection_settings.popup.countdownType',
                        value: e.target.checked ? 'recurring' : '',
                      },
                    })
                  }
                  checked={countdownType === 'recurring'}
                />
                {countdownType === 'recurring' && (
                  <>
                    <Select
                      transformValue
                      fullWidth
                      size="small"
                      margin="dense"
                      menuPosition="fixed"
                      options={WEEKDAYS}
                      value={countdownDay}
                      onChange={(v) =>
                        onInputChange({
                          target: {
                            name: 'collection_settings.popup.countdownDay',
                            value: v,
                          },
                        })
                      }
                    />
                    <Stack direction="row" alignItems="center" spacing={0}>
                      <Timepicker
                        size="small"
                        value={countdownTime}
                        onChange={(v) =>
                          onInputChange({
                            target: {
                              name: 'collection_settings.popup.countdownTime',
                              value: v,
                            },
                          })
                        }
                      />
                    </Stack>
                  </>
                )}
              </Box>
            </Box>
          </Box>
        </React.Fragment>
      )}
    </Box>
  );
};

export default PopUp;
